/**
 * Returns a function that adds created/updated metadata fields to an object.
 *
 * @param {string} createdBy - The user who created the object.
 * @param {string} createdAt - The creation timestamp (ISO string).
 * @return {Function} A function that takes an object and returns a new object with createdAt, createdBy, updatedAt, and updatedBy fields set to the provided values.
 */
export const addCreatedByAndAt =
  (createdBy: string, createdAt: string) =>
  <T extends { [key: string]: unknown }>(
    obj: T,
  ): T & {
    createdAt: string;
    createdBy: string;
    updatedAt: string;
    updatedBy: string;
  } => ({
    ...obj,
    createdAt,
    createdBy,
    updatedAt: createdAt,
    updatedBy: createdBy,
  });

/**
 * Returns a function that adds or updates the updatedAt/updatedBy fields on an object.
 * @param {string} updatedBy - The user who updated the object.
 * @param {string} updatedAt - The update timestamp (ISO string).
 * @return {Function} A function that takes an object and returns a new object with updatedAt and updatedBy fields set to the provided values.
 */
export const addUpdatedByAndAt =
  (updatedBy: string, updatedAt: string) =>
  <T extends { [key: string]: unknown }>(
    obj: T,
  ): T & { updatedAt: string; updatedBy: string } => ({
    ...obj,
    updatedAt,
    updatedBy,
  });
